// Types
import { IPageParam } from "@/Common/api/types/pagination";

// APIs
import { getRepairOrders } from "@/DataPages/api/repairOrders/queries";

// Hooks
import { usePaginatedResource } from "@/Common/hooks/Pagination";
import { buildGetNextPageParam } from "@/Common/hooks/Pagination/helpers";

// Helpers
import { and } from "@/Common/helpers/sonar";

// Constants
const PAGINATION_SIZE = 500;

export function getRepairOrdersQueryKey(args: { locationId: string; commissionCycleId: string; payPeriodId: string }) {
	const { locationId, commissionCycleId, payPeriodId } = args;
	return ["repairOrders", locationId, commissionCycleId, payPeriodId];
}

export function useRepairOrdersQuery(args: {
	enabled: boolean;
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	paginationSize?: number;
}) {
	const { enabled, locationId, commissionCycleId, payPeriodId, paginationSize = PAGINATION_SIZE } = args;

	const {
		data: repairOrdersData,
		isLoading,
		isFetching,
		hasNextPage,
		...otherResults
	} = usePaginatedResource({
		queryKey: getRepairOrdersQueryKey({ locationId, commissionCycleId, payPeriodId }),
		enabled: and(enabled, !!locationId, !!commissionCycleId, !!payPeriodId),
		initialPageParam: { after: "", first: paginationSize },
		getNextPageParam: buildGetNextPageParam(paginationSize),
		fetchAllPages: true,
		staleTime: Infinity,
		gcTime: Infinity,
		queryFn: async ({ pageParam }) => {
			const typedPageParam = pageParam as IPageParam<string>;
			if (!typedPageParam || !("after" in typedPageParam) || !("first" in typedPageParam)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}

			const paginatedResponse = await getRepairOrders({
				locationId,
				commissionCycleId,
				payPeriodId,
				first: typedPageParam.first,
				after: typedPageParam.after,
			});

			if (!paginatedResponse) {
				throw new Error("Error retrieving Repair Orders data");
			}

			return paginatedResponse;
		},
	});

	return {
		repairOrdersData,
		isLoading,
		isPaginating: isFetching || hasNextPage,
		...otherResults,
	};
}
